import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";

import type { NewProductDraftPatch, NewProductState, NfrKey } from "../newProduct/state";
import { NewProductScreen } from "../newProduct/NewProductScreen";
import {
    getContextMesh,
    updateContextMesh,
    type ContextMeshWorkspace,
    type UpdateContextMeshRequest
} from "./contextMeshApi";

function rows(value: string | null): string[] {
    return (value ?? "").split(/\r?\n/).map(item => item.trim()).filter(Boolean);
}

function objectValue(value: unknown): Record<string, unknown> | null {
    return typeof value === "object" && value !== null && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

function stringValue(value: unknown): string {
    return typeof value === "string" ? value : "";
}

function stringRows(value: unknown): string[] {
    return Array.isArray(value)
        ? value.filter((item): item is string => typeof item === "string")
        : [];
}

export function contextMeshRefreshPatch(workspace: ContextMeshWorkspace): NewProductDraftPatch {
    const primaryFormFactor = workspace.metadata.primaryFormFactor;
    const discovery = objectValue(workspace.metadata.discovery);
    const existingProduct = objectValue(discovery?.existingProduct);
    const creationType = workspace.metadata.creationType;
    const isExistingProductContext = existingProduct !== null ||
        creationType === "new_features" ||
        creationType === "new_single_feature";
    const featureScope = workspace.metadata.featureScope;
    return {
        kind: isExistingProductContext ? "existing" : "new",
        ...(isExistingProductContext
            ? {
                  scope: featureScope === "multi" || creationType === "new_features" ? "multi" as const : "single" as const,
                  existingProduct: {
                      name: stringValue(existingProduct?.name),
                      purpose: stringValue(existingProduct?.purpose),
                      users: stringRows(existingProduct?.users),
                      capabilities: stringValue(existingProduct?.capabilities)
                  }
              }
            : {}),
        productName: workspace.name,
        problemProductSolves: workspace.problemStatement ?? "",
        solutionProductOffers: workspace.solutionStatement ?? workspace.description ?? "",
        targetUsers: [...workspace.personas],
        teamMembers: rows(workspace.teamComposition),
        nonFunctionalRequirements: workspace.nonfunctionalRequirements as Partial<Record<NfrKey, string>>,
        primaryFormFactor: primaryFormFactor === "mobile" || primaryFormFactor === "api" ? primaryFormFactor : "web"
    };
}

export function buildContextMeshRefreshRequest(
    workspace: ContextMeshWorkspace,
    state: NewProductState
): UpdateContextMeshRequest {
    const teamMembers = state.team.map(item => item.trim()).filter(Boolean);
    const personas = state.users.map(item => item.trim()).filter(Boolean);
    const nonfunctionalRequirements = Object.fromEntries(
        Object.entries(state.nfr).map(([key, value]) => [key, value.trim()]).filter(([, value]) => Boolean(value))
    );
    const discovery = {
        ...(typeof workspace.metadata.discovery === "object" && workspace.metadata.discovery !== null
            ? workspace.metadata.discovery as Record<string, unknown>
            : {}),
        name: state.fields.name.trim(),
        problem: state.fields.problem.trim(),
        solution: state.fields.solution.trim(),
        targetUsers: personas,
        teamMembers,
        ...(state.kind === "existing"
            ? {
                  existingProduct: {
                      name: state.existingProduct.name.trim(),
                      purpose: state.existingProduct.purpose.trim(),
                      users: state.existingProduct.users.map(item => item.trim()).filter(Boolean),
                      capabilities: state.existingProduct.capabilities.trim()
                  }
              }
            : {})
    };
    const metadata = {
        ...workspace.metadata,
        primaryFormFactor: state.primaryFormFactor ?? "web",
        discovery
    };
    return {
        expectedVersion: workspace.version,
        name: state.fields.name.trim(),
        description: state.fields.solution.trim(),
        problemStatement: state.fields.problem.trim(),
        solutionStatement: state.fields.solution.trim(),
        personas,
        teamComposition: teamMembers.join("\n"),
        nonfunctionalRequirements,
        metadata
    };
}

export interface StandaloneContextMeshRefreshScreenProps {
    token?: string | null;
    contextMeshId: string;
    onCancel: () => void;
    onRefreshed: (contextMeshId: string) => void;
}

export function StandaloneContextMeshRefreshScreen({
    token,
    contextMeshId,
    onCancel,
    onRefreshed
}: StandaloneContextMeshRefreshScreenProps) {
    const [workspace, setWorkspace] = useState<ContextMeshWorkspace | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        setError(null);
        void getContextMesh(token, contextMeshId)
            .then(result => { if (!cancelled) setWorkspace(result); })
            .catch(reason => {
                if (!cancelled) setError(reason instanceof Error ? reason.message : "Unable to load the Context Mesh.");
            });
        return () => { cancelled = true; };
    }, [contextMeshId, token]);

    const initialDraftPatch = useMemo(
        () => workspace ? contextMeshRefreshPatch(workspace) : null,
        [workspace]
    );

    if (error) {
        return (
            <div className="np-v6" style={{ display: "grid", placeItems: "center", height: "100%" }}>
                <div role="alert">{error} <button type="button" onClick={onCancel}>Back</button></div>
            </div>
        );
    }
    if (!workspace || !initialDraftPatch) {
        return <div className="np-v6" style={{ display: "grid", placeItems: "center", height: "100%" }}><CircularProgress size={20} /></div>;
    }

    return (
        <NewProductScreen
            key={`refresh-context-${workspace.contextMeshId}-${workspace.version}`}
            mode="refresh-context-mesh"
            creationExperience="standalone-context-mesh"
            initialDraftPatch={initialDraftPatch}
            refreshContextTitle={workspace.name}
            onCancel={onCancel}
            onBackToContextMesh={onCancel}
            onCreated={() => undefined}
            onRefreshContextMesh={async state => {
                if (!token) throw new Error("Your session hasn't finished loading — try again in a moment.");
                await updateContextMesh(token, workspace.contextMeshId, buildContextMeshRefreshRequest(workspace, state));
            }}
            onRefreshContextMeshComplete={() => onRefreshed(workspace.contextMeshId)}
        />
    );
}
